import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";
import type { QueryTarget } from "@/shared/domain/query";
import type { ViewRecord } from "@/shared/domain/views";

import { viewQueryKeys } from "./viewQueryKeys";

/**
 * Every View available on one resource type. `target` is part of the key — a View
 * built on Files means nothing on Datasets.
 *
 * Reads through `ViewService.getAllViews`, the same call the route loader uses
 * to warm this cache slot: the picker's count is part of the toolbar's width,
 * so a loader seeding a different list than this hook would produce would
 * trade one layout shift for another.
 */
export function useViews(target: QueryTarget) {
  const { viewService } = useDomainServices();
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.org_id;

  return useQuery<ViewRecord[], Error>({
    queryKey: viewQueryKeys.list(orgId, target),
    queryFn: ({ signal }) => {
      if (orgId === undefined) {
        return Promise.reject(new Error("CurrentOrganization is not set"));
      }

      return viewService.getAllViews(target, orgId, { abortSignal: signal });
    },
  });
}
